import { Link } from "@tanstack/react-router";
import {
  Botao, Cartao, Selo, Medidor, Vazio, LinhaDoTempo, ItemDaLinha, Avatar, type TomDoSelo,
} from "@notes/ui";
import { CalendarClock, Mic, ListChecks, Clock, ShieldCheck, ArrowRight, BarChart3, TrendingUp } from "lucide-react";
import { useEu } from "../shell/Sessao.js";
import { Pagina, Secao, Numeros, Carregando, Erro } from "../shell/Shell.js";
import { usarReunioes, usarPendencias, usarResumoDoDia, mensagemDeErro } from "../lib/api.js";
import { dataHora, duracao, dataCurta } from "../lib/formato.js";

/**
 * HOJE. O dia do consultor em uma tela.
 *
 * A ordem não é estética, é de consequência: primeiro o número que ele precisa
 * saber sem procurar, depois o que ele PROMETEU (a única coisa aqui que vira
 * constrangimento com o cliente se passar batido), depois o que espera decisão
 * dele, e por fim as conversas recentes.
 */
export function Painel() {
  const eu = useEu();
  const reunioes = usarReunioes();
  const pendencias = usarPendencias();
  const resumo = usarResumoDoDia();
  const primeiroNome = (eu.nome ?? "").split(" ")[0] ?? "";

  if (reunioes.isError) {
    return <Pagina titulo="Hoje"><Erro mensagem={mensagemDeErro(reunioes.error)} aoTentarDeNovo={() => void reunioes.refetch()} /></Pagina>;
  }

  const hoje = new Date().toISOString().slice(0, 10);
  const promessas = pendencias.data?.promessas ?? [];
  const minhas = promessas.filter((p) => p.quem === "consultor");
  const doCliente = promessas.filter((p) => p.quem === "cliente");
  const paraAprovar = pendencias.data?.acoesParaAprovar ?? [];
  const ultimas = (reunioes.data?.reunioes ?? []).slice(0, 6);
  const n = resumo.data;

  return (
    <Pagina
      titulo={`Olá, ${primeiroNome || "consultor"}`}
      descricao="O que você prometeu, o que espera a sua decisão e as conversas mais recentes."
      acao={<Link to="/reunioes"><Botao tamanho="sm" variante="contorno">Ver todas as conversas<ArrowRight size={14} /></Botao></Link>}
    >
      <Secao ordem={0}>
        {resumo.isLoading || !n ? <Carregando tipo="cartoes" /> : (
          <Numeros itens={[
            { rotulo: `Conversas em ${n.dias} dias`, valor: n.numeros.reunioes, icone: <Mic size={13} />,
              nota: n.numeros.reunioes === 0 ? "nenhuma ainda" : undefined },
            { rotulo: "Horas ouvidas", valor: n.numeros.horas, icone: <Clock size={13} /> },
            { rotulo: "Sua média", valor: n.meuScoreMedio ?? "sem nota", icone: <BarChart3 size={13} />,
              nota: n.minhasConversasAvaliadas > 0 ? `de ${n.minhasConversasAvaliadas} conversa${n.minhasConversasAvaliadas === 1 ? "" : "s"} suas` : "nenhuma conversa sua foi avaliada" },
            { rotulo: "Promessas vencidas", valor: n.promessasVencidas, icone: <ListChecks size={13} />,
              alerta: n.promessasVencidas > 0,
              nota: `${n.promessasAbertas} em aberto` },
          ]} />
        )}
      </Secao>

      <Secao titulo="Você prometeu" ordem={1}
        acao={minhas.length > 0 ? <Link to="/pendencias" className="enotes-linha-link">ver todas</Link> : undefined}>
        {pendencias.isLoading ? <Carregando /> : minhas.length === 0 ? (
          <Vazio titulo="Nada em aberto"
            descricao="Toda promessa feita nas suas reuniões já foi cumprida. Quando uma conversa gerar um combinado, ele aparece aqui com o prazo." />
        ) : (
          <Cartao>
            <LinhaDoTempo>
              {minhas.slice(0, 6).map((p) => {
                const atrasada = !!p.prazo && p.prazo < hoje;
                return (
                  <ItemDaLinha
                    key={p.id}
                    tom={atrasada ? "critico" : "atencao"}
                    icone={atrasada ? <Clock size={15} /> : <ListChecks size={15} />}
                    titulo={p.descricao}
                    quando={p.prazo ? (atrasada ? `venceu em ${dataCurta(p.prazo)}` : dataCurta(p.prazo)) : "sem prazo"}
                    acao={p.reuniaoId
                      ? <Link to="/reunioes/$id" params={{ id: p.reuniaoId }} className="enotes-linha-link">ouvir a conversa</Link>
                      : undefined}
                  >
                    <p className="enotes-lt-nota">
                      {atrasada ? "Passou do prazo. O cliente está esperando." : "Fecha sozinha quando a prova chegar do CRM."}
                    </p>
                  </ItemDaLinha>
                );
              })}
            </LinhaDoTempo>
          </Cartao>
        )}
      </Secao>

      {paraAprovar.length > 0 && (
        <Secao titulo="Esperando a sua decisão" ordem={2}
          acao={<Link to="/aprovacoes" className="enotes-linha-link">abrir aprovações</Link>}>
          <Cartao className="enotes-chamada">
            <span className="enotes-chamada-icone" aria-hidden><ShieldCheck size={18} /></span>
            <div>
              <p className="enotes-chamada-titulo">
                {paraAprovar.length === 1
                  ? "Uma ação proposta pelas conversas"
                  : `${paraAprovar.length} ações propostas pelas conversas`}
              </p>
              <p className="enotes-chamada-sub">
                Nada acontece no CRM antes de você conferir a evidência e aprovar.
              </p>
            </div>
            <Link to="/aprovacoes"><Botao variante="primario" tamanho="sm">Revisar<ArrowRight size={14} /></Botao></Link>
          </Cartao>
        </Secao>
      )}

      {doCliente.length > 0 && (
        <Secao titulo="O cliente ficou de" ordem={3}>
          <Cartao>
            <LinhaDoTempo>
              {doCliente.slice(0, 5).map((p) => (
                <ItemDaLinha key={p.id} tom="info" icone={<TrendingUp size={15} />} titulo={p.descricao}
                  quando={p.prazo ? dataCurta(p.prazo) : "sem prazo"} />
              ))}
            </LinhaDoTempo>
          </Cartao>
        </Secao>
      )}

      <Secao titulo="Últimas conversas" ordem={4}>
        {reunioes.isLoading ? <Carregando tipo="cartoes" /> : ultimas.length === 0 ? (
          <Vazio
            titulo="Nenhuma reunião ainda"
            descricao="Assim que a primeira gravação entrar, ela aparece aqui com resumo, nota da conversa e o que ficou combinado."
            acao={<Link to="/gravar"><Botao variante="primario" tamanho="sm">Configurar a gravação</Botao></Link>}
          />
        ) : (
          <ul className="enotes-cartoes">
            {ultimas.map((r, i) => (
              <li key={r.id} className="surge" style={{ animationDelay: `${i * 45}ms` }}>
                <Link to="/reunioes/$id" params={{ id: r.id }} className="enotes-cartao-reuniao">
                  <span className="enotes-cr-topo">
                    <span className="enotes-cr-titulo">{r.titulo}</span>
                    <EstadoDaReuniao status={r.status} />
                  </span>
                  <span className="enotes-cr-meta carimbo">
                    <CalendarClock size={12} aria-hidden /> {dataHora(r.inicioEm)} · {duracao(r.duracaoS)}
                  </span>
                  {r.negociacaoCrmId && (
                    <span className="enotes-cr-vinculo">
                      <Selo tom="marca">negociação {r.negociacaoCrmId}</Selo>
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Secao>
    </Pagina>
  );
}

const ESTADOS: Record<string, { rotulo: string; tom: TomDoSelo }> = {
  agendada: { rotulo: "Agendada", tom: "info" },
  gravando: { rotulo: "Gravando", tom: "critico" },
  processando: { rotulo: "Processando", tom: "atencao" },
  concluida: { rotulo: "Pronta", tom: "bom" },
  falhou: { rotulo: "Com problema", tom: "critico" },
  descartada: { rotulo: "Descartada", tom: "neutro" },
};

export function EstadoDaReuniao({ status }: { status: string }) {
  const e = ESTADOS[status] ?? { rotulo: status, tom: "neutro" as TomDoSelo };
  return <Selo tom={e.tom}><span aria-hidden className={`sinal sinal-${e.tom === "bom" ? "bom" : e.tom === "critico" ? "critico" : "atencao"}`} />{e.rotulo}</Selo>;
}

/** A nota da conversa como a tela mostra em lista: número e faixa, sem gráfico. */
export function NotaDaConversa({ total }: { total: number | null | undefined }) {
  if (total == null) return <span className="carimbo">sem nota</span>;
  const tom: TomDoSelo = total >= 80 ? "bom" : total >= 60 ? "atencao" : "critico";
  return <Medidor valor={total} rotulo="" tom={tom} />;
}
